import { Component } from "@theme/component";
import { DialogComponent } from "@theme/dialog";

const API_REVISION = "2026-07-15";

class BackInStock extends Component {
  connectedCallback() {
    const emailInput = this.emailInput;
    emailInput?.addEventListener("input", () => this.#resetError());
  }

  async handleSubmit() {
    this.#resetError();

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const email = this.#getAndValidationEmail();
    if (!email) return;

    const subscribeSuccess = await this.#subscribeToBackInStock(
      email,
      variantId,
    );
    if (!subscribeSuccess) return;

    if (this.emailOptIn == true) {
      this.#optInToEmail(email);
    }

    this.#showSuccess();
  }

  /**
   * Show the success message
   */
  #showSuccess() {
    this.setAttribute('showSuccess', '');
  }

  /**
   * Hide the success message
   */
  #resetSuccess() {
    this.removeAttribute('showSuccess');
  }

  /**
   * Reset the form fields
   */
  #resetForm() {
    const emailInput = this.emailInput;
    if (emailInput) emailInput.value = '';

    const optIn = this.emailOptInCheckbox;
    if (optIn) optIn.checked = false;
  }

  /**
   * Read and validate the email input
   */
  #getAndValidationEmail() {
    const input = this.emailInput;
    if (!input?.reportValidity()) return undefined;

    return input.value;
  }

  /**
   * Close the dialog
   * @param {Event} event - The event.
   */
  close(event) {
    // Prevent the product form from opening the cart drawer dialog
    event?.preventDefault();

    this.#resetError();

    const dialog = this.querySelector("dialog-component");
    if (!(dialog instanceof DialogComponent)) return;
    dialog.closeDialog();

    this.#resetSuccess();
    this.#resetForm();
  }

  /**
   * Open the dialog
   */
  show() {
    this.#resetForm();
    this.#resetSuccess();

    const dialog = this.querySelector("dialog-component");
    if (!(dialog instanceof DialogComponent)) return;
    dialog.showDialog();
  }

  /**
   * Make the API request to subscribe for a back in stock notification
   * @param {string} email
   * @param {string} variantId
   */
  async #subscribeToBackInStock(email, variantId) {
    const payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
              },
            },
          },
          channels: ["EMAIL"],
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    const requestOptions = {
      method: "POST",
      headers: {
        revision: API_REVISION,
        accept: "application/vnd.api+json",
        "content-type": "application/vnd.api+json",
      },
      body: JSON.stringify(payload),
    };
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.siteId}`,
        requestOptions,
      );
      if (!response.ok) {
        const errorData = await response.json();
        this.#displayError(response.status, errorData.errors);
        return false;
      }
      return true;
    } catch (error) {
      console.log("error", error);
      return false;
    }
  }

  /**
   * Show a tooltip with the error message
   * @param {number} status
   * @param {any[]} errors
   */
  #displayError(status, errors) {
    if (errors.length == 0) return;

    const validationTooltip = this.validationTooltip;
    if (!validationTooltip) return;

    let errorMsg = undefined;
    if (status == 400) {
      errorMsg = errors[0]?.detail;
    }

    validationTooltip.textContent = errorMsg ?? "Invalid";
    validationTooltip.classList.add("show");
  }

  /**
   * Hide the error tooltip
   */
  #resetError() {
    const validationTooltip = this.validationTooltip;
    if (!validationTooltip) return;
    validationTooltip.classList.remove("show");
  }

  /**
   * Make the API request to opt in to marketing emails
   * @param {string} email
   */
  #optInToEmail(email) {
    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                subscriptions: {
                  email: { marketing: { consent: "SUBSCRIBED" } },
                },
                email: email,
              },
            },
          },
          custom_source: this.dataset.customSource,
        },
        relationships: { list: { data: { type: "list", id: this.listId } } },
      },
    };
    const options = {
      method: "POST",
      headers: {
        revision: API_REVISION,
        accept: "application/vnd.api+json",
        "content-type": "application/vnd.api+json",
      },
      body: JSON.stringify(payload),
    };

    fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${this.siteId}`,
      options,
    ).catch((err) => console.error(err));
  }

  get siteId() {
    return this.dataset.siteId ?? "";
  }

  get listId() {
    return this.dataset.listId ?? "";
  }

  get validationTooltip() {
    return this.querySelector('[ref="emailValidation"]');
  }

  get emailInput() {
    const input = this.querySelector(`#${this.dataset.formId}-back-in-stock__email`);
    if (!(input instanceof HTMLInputElement)) return undefined;
    return input;
  }

  get emailOptInCheckbox() {
    const checkbox = this.querySelector(`#${this.dataset.formId}-opt_in_email_consent`);
    if (!(checkbox instanceof HTMLInputElement)) return undefined;
    return checkbox;
  }

  get emailOptIn() {
    const checkbox = this.emailOptInCheckbox;
    return checkbox?.checked;
  }
}

if (!customElements.get("back-in-stock-component")) {
  customElements.define("back-in-stock-component", BackInStock);
}
